/**
 * Crimson Mandate - Phaser 3 Entry Point
 */

import Phaser from 'phaser';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { MainMenuScene } from './scenes/menu/MainMenuScene';
import { OptionsScene } from './scenes/menu/OptionsScene';
// MMO scenes
import { MMOLoginScene } from './scenes/menu/MMOLoginScene';
import { MMORegisterScene } from './scenes/menu/MMORegisterScene';
import { MMOGameScene } from './scenes/mmo/MMOGameScene';
import { MMOHUDScene } from './scenes/mmo/MMOHUDScene';
import { MMOHUDMobileScene } from './scenes/mmo/MMOHUDMobileScene';
import { MemoryManager } from './managers/MemoryManager';

// Single-instance enforcement — block duplicate tabs
const instanceLock = new MemoryManager();
if (!instanceLock.acquireInstanceLock()) {
  const container = document.getElementById('game-container');
  if (container) {
    container.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100vh;color:#ff4444;font-family:monospace;font-size:1.5rem;text-align:center;padding:2rem;">
      <div>
        <h2>DUPLICATE TAB DETECTED</h2>
        <p>Crimson Mandate is already running in another tab.</p>
        <p>Please close this tab and return to the existing game session.</p>
      </div>
    </div>`;
  }
  throw new Error('Duplicate instance blocked');
}

// Game configuration
const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  backgroundColor: '#000000',
  scale: {
    mode: Phaser.Scale.RESIZE,
    // NOTE: autoCenter intentionally omitted. In RESIZE mode the canvas already
    // fills its parent (#game-container is inset:0), so there's nothing to
    // center. Leaving autoCenter on added a sub-pixel margin offset that was
    // invisible at the center of the screen but grew into missed/skewed taps
    // near the edges.
    autoCenter: Phaser.Scale.NO_CENTER,
    width: '100%',
    height: '100%',
  },
  scene: [
    BootScene,
    PreloadScene,
    MainMenuScene,
    OptionsScene,
    // MMO scenes
    MMOLoginScene,
    MMORegisterScene,
    MMOGameScene,
    MMOHUDScene,
    MMOHUDMobileScene,
  ],
  render: {
    pixelArt: false,
    antialias: true,
    antialiasGL: true,
    roundPixels: true,
    maxTextures: 512,
  },
  input: {
    touch: {
      target: undefined,
      capture: true,
    },
  },
  autoFocus: true,
  dom: {
    createContainer: true,
  },
};

// Create game instance
const game = new Phaser.Game(config);

// Export for debugging
(window as unknown as { game: Phaser.Game }).game = game;

// Workaround for Phaser 3.90.0 game loop issues:
//
// Problem 1: game.step() skips scene manager updates when hasFocus is false.
// Problem 2: The browser throttles requestAnimationFrame for unfocused tabs,
//   so the game loop may never actually tick (loop.frame stays 0).
// Problem 3: scene.start() called during create() is silently dropped because
//   the scene manager hasn't begun processing pending operations yet.
//
// Fix: Override onHidden/onBlur to prevent Phaser from clearing hasFocus,
// and use a setInterval-based fallback that pumps game.step() directly
// when the rAF loop isn't ticking. The fallback stops once the real loop
// takes over (loop.frame starts incrementing) or the login scene is reached.

// Override visibility handlers immediately (before they fire).
// Phaser sets hasFocus=false in onHidden/onBlur which causes game.step()
// to skip scene manager updates entirely.
(game as any).onHidden = () => { /* no-op: keep game running */ };
(game as any).onBlur = () => { /* no-op: keep focus */ };
(game as any).hasFocus = true;

// Fallback game loop: pumps the game via setInterval when rAF isn't ticking.
// Chrome throttles both rAF (to 0fps) and setInterval (to 1s) for background
// tabs. We use real elapsed time as the delta so scene timers advance correctly
// even when calls are infrequent.
let lastFallbackTime = 0;
const fallbackLoop = setInterval(() => {
  if (!game.isBooted) return;

  const now = performance.now();
  if (!lastFallbackTime) lastFallbackTime = now;

  // Once the real rAF loop is ticking steadily, stop the fallback
  if (game.loop.frame > 10) {
    console.log('Phaser rAF loop running (frame:', game.loop.frame, '), stopping fallback');
    clearInterval(fallbackLoop);
    return;
  }

  // Stop after 30 seconds regardless
  if (now - lastFallbackTime > 30000) {
    clearInterval(fallbackLoop);
    return;
  }

  // Use real elapsed time as delta (capped at 1s to avoid huge jumps)
  const delta = Math.min(now - lastFallbackTime, 1000) || 16;
  lastFallbackTime = now;

  // Ensure hasFocus stays true
  (game as any).hasFocus = true;
  (game as any).isRunning = true;

  // Boot the scene manager if needed
  const sceneManager = game.scene as any;
  if (!sceneManager.isBooted) {
    if (typeof sceneManager.processQueue === 'function') {
      sceneManager.processQueue();
    }
    sceneManager.isBooted = true;
  }

  // Start BootScene if no scene is active yet
  const anyActive = game.scene.scenes.some(
    (s: Phaser.Scene) => {
      const status = (s as any).sys.settings.status;
      return status >= 2 && status <= 5;
    }
  );
  if (!anyActive) {
    const boot = game.scene.getScene('BootScene') as any;
    if (boot?.sys.settings.status <= 1) {
      game.scene.start('BootScene');
    }
  }

  // Pump the game with real delta so scene timers advance correctly.
  // Call both game.step() and scene.update() — game.step() handles the
  // full lifecycle (renderer, events) while scene.update() ensures the
  // scene manager processes pending transitions and timers.
  game.step(now, delta);
  sceneManager.update(now, delta);
}, 16); // requested 60fps, browser may throttle to 1s for background tabs

// CRITICAL: Delayed scale refresh to fix input coordinate alignment
// At 50ms the canvas may not be fully positioned/centered by the browser.
// Wait for scenes to initialize and layout to settle before refreshing scale bounds.
setTimeout(() => {
  if (game.isRunning) {
    game.scale.refresh();
    console.log('Scale refreshed after scenes initialized - input bounds updated');
  }
}, 500);

// Also refresh on window resize to keep input coordinates aligned
window.addEventListener('resize', () => {
  if (game.isRunning) {
    game.scale.refresh();
  }
});

// Mobile: the visual viewport shifts when the URL bar hides/shows or the
// on-screen keyboard appears, but window 'resize' doesn't always fire for
// those changes. Without refreshing canvas bounds, Phaser's input coordinates
// drift — taps near the center still land correctly, but taps near the edges
// miss or register at the wrong position. visualViewport fires for all of
// these cases, so we refresh scale here too.
const vv = (window as Window & { visualViewport?: VisualViewport }).visualViewport;
if (vv) {
  const refreshFromViewport = () => {
    if (game.isRunning) {
      game.scale.refresh();
    }
  };
  vv.addEventListener('resize', refreshFromViewport);
  vv.addEventListener('scroll', refreshFromViewport);
}

// Orientation changes on mobile (portrait <-> landscape) also require a
// bounds refresh. Some browsers fire 'orientationchange' before layout has
// settled, so we schedule the refresh on the next frame.
window.addEventListener('orientationchange', () => {
  requestAnimationFrame(() => {
    if (game.isRunning) {
      game.scale.refresh();
    }
  });
});

// iOS Safari ignores `user-scalable=no` in some scenarios (accessibility
// zoom, double-tap zoom, explicit pinch). When the visual viewport is
// scaled (>1) or panned (>0 pageLeft/pageTop), taps are mapped against
// the visual viewport while Phaser maps against the layout viewport —
// producing a linear offset that grows with distance from the zoomed
// focal point. Detect this and either reset the zoom (via a scroll trick)
// or warn in the console so we can diagnose.
if (vv) {
  const resetPageZoom = () => {
    // Meta viewport hot-swap is the only reliable way to force Safari back
    // to scale=1 programmatically.
    const meta = document.querySelector('meta[name=viewport]') as HTMLMetaElement | null;
    if (!meta) return;
    const original = meta.content;
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover';
    // Force a reflow so Safari re-evaluates
    void document.body.offsetHeight;
    // Restore after one frame so dynamic changes keep working
    requestAnimationFrame(() => { meta.content = original; });
  };

  const onVisualZoom = () => {
    if (!vv) return;
    const scale = vv.scale;
    const offX = vv.offsetLeft;
    const offY = vv.offsetTop;
    if (scale > 1.005 || Math.abs(offX) > 0.5 || Math.abs(offY) > 0.5) {
      console.warn(
        '[Input] Visual viewport is zoomed or panned — taps will be skewed.',
        { scale, offX, offY, pageLeft: vv.pageLeft, pageTop: vv.pageTop },
      );
      resetPageZoom();
    }
    if (game.isRunning) {
      game.scale.refresh();
    }
  };
  vv.addEventListener('resize', onVisualZoom);
  vv.addEventListener('scroll', onVisualZoom);
}

// Force a canvas-bounds refresh at the very START of every tap (capture
// phase, before Phaser processes the event). This guarantees the input
// manager is mapping against the canvas's actual current position/size —
// even if something caused a sub-pixel shift since the last resize event.
// The cost is microseconds per tap.
// Note: this MUST be on 'window' in capture phase so it fires before
// Phaser's own listeners on the canvas.
const refreshOnTap = () => {
  if (game.isRunning) game.scale.refresh();
};
window.addEventListener('pointerdown', refreshOnTap, { capture: true, passive: true });
window.addEventListener('touchstart',  refreshOnTap, { capture: true, passive: true });

// Diagnostic: log raw pointerdown coordinates vs canvas bounds so we can
// verify edge clicks aren't being mapped elsewhere. Toggle via the URL
// query `?debugInput=1` so it stays off in production by default.
if (new URLSearchParams(window.location.search).has('debugInput')) {
  window.addEventListener('pointerdown', (e) => {
    const canvas = game.canvas;
    if (!canvas) return;
    const r = canvas.getBoundingClientRect();
    const inCanvas = e.target === canvas;
    const relX = e.clientX - r.left;
    const relY = e.clientY - r.top;
    const edgeX = Math.min(relX, r.width - relX);
    const edgeY = Math.min(relY, r.height - relY);
    const vvScale = vv?.scale ?? 1;
    console.log('[pointerdown]', {
      target: (e.target as Element | null)?.tagName ?? '?',
      inCanvas,
      client: [e.clientX, e.clientY],
      canvas: [Math.round(relX), Math.round(relY)],
      rect: [Math.round(r.left), Math.round(r.top), Math.round(r.width), Math.round(r.height)],
      edgePx: [Math.round(edgeX), Math.round(edgeY)],
      vvScale,
      dpr: window.devicePixelRatio,
    });
  }, true);
}

// Re-enable game when returning to the tab after being backgrounded.
// Browsers throttle/suspend background tabs, which can cause Phaser's
// input system to lose state and the WebSocket to disconnect.
document.addEventListener('visibilitychange', () => {
  if (document.hidden || !game.isRunning) return;

  console.log('[Visibility] Tab restored — re-enabling game systems');

  // Restore Phaser focus state
  (game as any).hasFocus = true;
  (game as any).isRunning = true;

  // Re-enable input on all active scenes
  for (const scene of game.scene.scenes) {
    if (scene.sys.settings.active && scene.input) {
      scene.input.enabled = true;
      if (scene.input.keyboard) {
        scene.input.keyboard.enabled = true;
      }
    }
  }

  // Refresh scale/input bounds (window may have resized while hidden)
  game.scale.refresh();

  // Give canvas focus so keyboard events register
  game.canvas.focus();
});

console.log('Crimson Mandate initialized');
